const ENTITY_TYPE_COMPANY = "company";
const ENTITY_TYPE_LEAD = "lead";
const ENTITY_TYPE_CONTACT = "contact";

const TEXT_LIMIT = 320;
const NAME_LIMIT = 180;
const SHORT_TEXT_LIMIT = 120;
const URL_LIMIT = 2048;
const LONG_TEXT_LIMIT = 12000;

function safeTrim(value) {
  return String(value ?? "").trim();
}

function clampText(value, max = TEXT_LIMIT) {
  const text = safeTrim(value);
  if (!text) return "";
  return text.length > max ? text.slice(0, max) : text;
}

function normalizeEntityType(value, fallback = ENTITY_TYPE_LEAD) {
  const normalized = safeTrim(value || fallback).toLowerCase();
  if (normalized === ENTITY_TYPE_COMPANY) return ENTITY_TYPE_COMPANY;
  if (normalized === ENTITY_TYPE_CONTACT) return ENTITY_TYPE_CONTACT;
  return ENTITY_TYPE_LEAD;
}

function resolveId(value) {
  if (value === null || value === undefined) return null;
  const text = safeTrim(value);
  if (!text) return null;
  const numericValue = Number(text);
  return Number.isFinite(numericValue) && String(numericValue) === text ? numericValue : value;
}

function setText(target, key, value, max = TEXT_LIMIT) {
  const text = clampText(value, max);
  if (!text) return;
  target[key] = text;
}

function setFiniteNumber(target, keys, value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return;
  for (const key of Array.isArray(keys) ? keys : [keys]) {
    target[key] = numericValue;
  }
}

function firstFiniteNumber(...values) {
  for (const value of values) {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) return numericValue;
  }
  return null;
}

function resolveEntityId(company = {}, entityType = ENTITY_TYPE_LEAD) {
  if (entityType === ENTITY_TYPE_COMPANY) {
    return resolveId(company?.id ?? company?.company_id ?? company?.companyId);
  }
  if (entityType === ENTITY_TYPE_CONTACT) {
    return resolveId(company?.id ?? company?.contact_id ?? company?.contactId);
  }
  return resolveId(company?.id ?? company?.lead_id ?? company?.leadId ?? company?.company_id ?? company?.companyId);
}

export function buildOpenCompanyNavEntity(company = {}, options = {}) {
  if (!company || typeof company !== "object") return null;

  const entityType = normalizeEntityType(options?.entityType || company?.entityType);
  const id = resolveEntityId(company, entityType);
  if (id === null || id === undefined || safeTrim(id) === "") return null;

  const navEntity = {
    id,
    entityType,
  };

  const companyId = resolveId(
    company?.company_id ?? company?.companyId ?? (entityType === ENTITY_TYPE_COMPANY ? id : null)
  );
  if (companyId !== null && companyId !== undefined) {
    navEntity.company_id = companyId;
    navEntity.companyId = companyId;
  }

  const leadId = resolveId(
    company?.lead_id ?? company?.leadId ?? (entityType === ENTITY_TYPE_LEAD ? id : null)
  );
  if (leadId !== null && leadId !== undefined) {
    navEntity.lead_id = leadId;
    navEntity.leadId = leadId;
  }

  const linkedJobId = firstFiniteNumber(
    company?.job_id,
    company?.jobId,
    company?.related_job_id,
    company?.relatedJobId,
    company?.matched_job_id,
    company?.matchedJobId
  );
  if (linkedJobId !== null) {
    setFiniteNumber(
      navEntity,
      ["job_id", "jobId", "related_job_id", "relatedJobId", "matched_job_id", "matchedJobId"],
      linkedJobId
    );
  }

  setText(navEntity, "job_title", company?.job_title || company?.jobTitle || company?.title, NAME_LIMIT);
  setText(navEntity, "jobTitle", company?.jobTitle || company?.job_title || company?.title, NAME_LIMIT);
  setText(navEntity, "title", company?.title || company?.job_title || company?.jobTitle, NAME_LIMIT);
  setText(navEntity, "contact_name", company?.contact_name || company?.name, NAME_LIMIT);
  setText(
    navEntity,
    "lead_link",
    company?.lead_link || company?.leadLink || company?.job_url || company?.post_link,
    URL_LIMIT
  );
  setText(navEntity, "other_link", company?.other_link || company?.otherLink, URL_LIMIT);

  setText(navEntity, "name", company?.name || company?.company_name, NAME_LIMIT);
  setText(navEntity, "company_name", company?.company_name || company?.name, NAME_LIMIT);
  setText(navEntity, "logo", company?.logo || company?.company_logo, URL_LIMIT);
  setText(navEntity, "company_logo", company?.company_logo || company?.logo, URL_LIMIT);
  setText(navEntity, "website", company?.website || company?.website_url || company?.company_website, URL_LIMIT);
  setText(
    navEntity,
    "website_url",
    company?.website_url || company?.website || company?.company_website,
    URL_LIMIT
  );
  setText(
    navEntity,
    "company_website",
    company?.company_website || company?.website || company?.website_url,
    URL_LIMIT
  );
  setText(navEntity, "company_link", company?.company_link, URL_LIMIT);
  setText(navEntity, "career_site", company?.career_site || company?.career_website, URL_LIMIT);
  setText(navEntity, "career_website", company?.career_website || company?.career_site, URL_LIMIT);
  setText(navEntity, "industry", company?.industry || company?.industry_sector, SHORT_TEXT_LIMIT);
  setText(navEntity, "industry_sector", company?.industry_sector || company?.industry, SHORT_TEXT_LIMIT);
  setText(navEntity, "company_size", company?.company_size || company?.size_range, SHORT_TEXT_LIMIT);
  setText(navEntity, "ownership_type", company?.ownership_type, SHORT_TEXT_LIMIT);
  setText(navEntity, "status", company?.status, SHORT_TEXT_LIMIT);
  setText(
    navEntity,
    "owner",
    company?.owner || company?.owner_name || company?.team_member,
    SHORT_TEXT_LIMIT
  );
  setText(
    navEntity,
    "owner_name",
    company?.owner_name || company?.owner || company?.team_member,
    SHORT_TEXT_LIMIT
  );
  setText(
    navEntity,
    "team_member",
    company?.team_member || company?.owner_name || company?.owner,
    SHORT_TEXT_LIMIT
  );
  setText(navEntity, "location", company?.location, NAME_LIMIT);
  setText(navEntity, "source", company?.source, SHORT_TEXT_LIMIT);
  setText(navEntity, "contact_email", company?.contact_email || company?.email, NAME_LIMIT);
  setText(navEntity, "contact_phone", company?.contact_phone || company?.phone, SHORT_TEXT_LIMIT);
  setText(navEntity, "linkedin_url", company?.linkedin_url, URL_LIMIT);
  setText(navEntity, "facebook_url", company?.facebook_url, URL_LIMIT);
  setText(navEntity, "twitter_url", company?.twitter_url || company?.x_url, URL_LIMIT);
  setText(navEntity, "instagram_url", company?.instagram_url, URL_LIMIT);
  setText(navEntity, "youtube_url", company?.youtube_url, URL_LIMIT);
  setText(navEntity, "tiktok_url", company?.tiktok_url, URL_LIMIT);
  setText(navEntity, "createdAt", company?.createdAt || company?.created_at, SHORT_TEXT_LIMIT);
  setText(navEntity, "created_at", company?.created_at || company?.createdAt, SHORT_TEXT_LIMIT);
  setText(
    navEntity,
    "lastActivity",
    company?.lastActivity || company?.lastActivityType || company?.last_activity_type,
    SHORT_TEXT_LIMIT
  );
  setText(
    navEntity,
    "lastActivityType",
    company?.lastActivityType || company?.last_activity_type || company?.lastActivity,
    SHORT_TEXT_LIMIT
  );
  setText(
    navEntity,
    "last_activity_type",
    company?.last_activity_type || company?.lastActivityType || company?.lastActivity,
    SHORT_TEXT_LIMIT
  );
  setText(
    navEntity,
    "last_activity_time",
    company?.last_activity_time || company?.updated_at || company?.created_at,
    SHORT_TEXT_LIMIT
  );
  setText(navEntity, "last_activity_message", company?.last_activity_message, TEXT_LIMIT);
  setText(navEntity, "lastSync", company?.lastSync, SHORT_TEXT_LIMIT);
  setText(navEntity, "notes", company?.notes, LONG_TEXT_LIMIT);
  setText(
    navEntity,
    "company_description",
    company?.company_description || company?.full_company_description,
    LONG_TEXT_LIMIT
  );
  setText(
    navEntity,
    "full_company_description",
    company?.full_company_description || company?.company_description,
    LONG_TEXT_LIMIT
  );
  setText(navEntity, "elevator_pitch", company?.elevator_pitch, 2000);
  setText(navEntity, "mission_and_vision", company?.mission_and_vision, 5000);
  setText(navEntity, "key_products_services", company?.key_products_services, 5000);
  setText(navEntity, "unique_selling_points", company?.unique_selling_points, 5000);
  setText(navEntity, "employee_perks", company?.employee_perks, 5000);
  setText(navEntity, "achievements_awards", company?.achievements_awards, 5000);
  if (Array.isArray(company?.contacts)) {
    navEntity.contacts = company.contacts;
  }
  if (Array.isArray(company?.related_leads)) {
    navEntity.related_leads = company.related_leads;
  }
  if (Array.isArray(company?.jobs)) {
    navEntity.jobs = company.jobs;
  }
  if (Array.isArray(company?.activities)) {
    navEntity.activities = company.activities;
  }
  if (Array.isArray(company?.candidates)) {
    navEntity.candidates = company.candidates;
  }
  if (company?.contact_repair_pending) {
    navEntity.contact_repair_pending = true;
  }
  if (company?.company_profile_enrichment_pending) {
    navEntity.company_profile_enrichment_pending = true;
  }

  const jobsCount = firstFiniteNumber(
    company?.jobsCount,
    company?.jobs_count,
    company?.jobOpenings,
    company?.job_openings
  );
  if (jobsCount !== null) {
    setFiniteNumber(navEntity, ["jobsCount", "jobs_count", "jobOpenings", "job_openings"], jobsCount);
  }

  const leadsCount = firstFiniteNumber(company?.leadsCount, company?.leads_count);
  if (leadsCount !== null) {
    setFiniteNumber(navEntity, ["leadsCount", "leads_count"], leadsCount);
  }

  const contactsCount = firstFiniteNumber(company?.contactsCount, company?.contacts_count);
  if (contactsCount !== null) {
    setFiniteNumber(navEntity, ["contactsCount", "contacts_count"], contactsCount);
  }

  const candidatesCount = firstFiniteNumber(company?.candidatesCount, company?.candidates_count);
  if (candidatesCount !== null) {
    setFiniteNumber(navEntity, ["candidatesCount", "candidates_count"], candidatesCount);
  }

  const activitiesCount = firstFiniteNumber(company?.activitiesCount, company?.activities_count);
  if (activitiesCount !== null) {
    setFiniteNumber(navEntity, ["activitiesCount", "activities_count"], activitiesCount);
  }

  return navEntity;
}

export function buildOpenCompanyNavPayload({
  company,
  defaultTab = "details",
  returnListTab = "",
  entityType,
  parentCompany = null,
  parentTab = "",
} = {}) {
  const openCompany = buildOpenCompanyNavEntity(company, { entityType });
  if (!openCompany?.id) return null;

  const payload = {
    viewMode: "leadTabsLayout",
    defaultTab: clampText(defaultTab || "details", SHORT_TEXT_LIMIT) || "details",
    openCompany,
    parentCompany: null,
    parentTab: "",
  };

  const normalizedReturnListTab = clampText(returnListTab, SHORT_TEXT_LIMIT);
  if (normalizedReturnListTab) {
    payload.returnListTab = normalizedReturnListTab;
  }

  const normalizedParentCompany = buildOpenCompanyNavEntity(parentCompany, {
    entityType: ENTITY_TYPE_COMPANY,
  });
  if (normalizedParentCompany?.id) {
    payload.parentCompany = normalizedParentCompany;
    payload.parentTab = clampText(parentTab, SHORT_TEXT_LIMIT);
  }

  return payload;
}
